import React from 'react';
import { AuthProvider } from './auth/AuthContext';
import { RouterProvider, useRouter, Link } from './router/Router';
import {
  PublicRoute,
  ProtectedRoute,
  StudentRoute,
  ProfileCompletedRoute,
  AdminRoute,
} from './auth/RouteGuards';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { CompleteProfile } from './components/auth/CompleteProfile';
import { StudentArea } from './components/student/StudentArea';
import { LandingPage } from './components/landing/LandingPage';
import { AdminLayout } from './components/admin/AdminLayout';
import { OpeningExperience } from './components/common/OpeningExperience';
import './App.css';

/**
 * Central Route Dispatcher handling path changes and route guards
 */
const AppRoutes: React.FC = () => {
  const { currentPath } = useRouter();

  // 1. Landing Page (World-class OTO STUDENTS Animated Interface)
  if (currentPath === '/') {
    return <LandingPage />;
  }

  // 2. Public Auth Routes
  if (currentPath === '/login') {
    return (
      <PublicRoute>
        <Login />
      </PublicRoute>
    );
  }

  if (currentPath === '/register') {
    return (
      <PublicRoute>
        <Register />
      </PublicRoute>
    );
  }

  if (currentPath === '/forgot-password') {
    return (
      <PublicRoute>
        <ForgotPassword />
      </PublicRoute>
    );
  }

  // 3. Profile Completion Route
  if (currentPath === '/complete-profile') {
    return (
      <ProtectedRoute>
        <CompleteProfile />
      </ProtectedRoute>
    );
  }

  // 4. Admin Routes (/admin, /admin/payments, /admin/bookings, /admin/students, etc.)
  if (currentPath.startsWith('/admin')) {
    return (
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    );
  }

  // 5. Student Protected Routes (/student, /student/bookings, /student/trips, etc.)
  if (currentPath.startsWith('/student')) {
    return (
      <StudentRoute>
        <ProfileCompletedRoute>
          <StudentArea />
        </ProfileCompletedRoute>
      </StudentRoute>
    );
  }

  // 6. 404 Not Found fallback
  return (
    <div className="stub-view-card" dir="rtl" style={{ margin: '5rem auto' }}>
      <div className="stub-icon">🔍</div>
      <h3>الصفحة غير موجودة (404)</h3>
      <p>عذرًا، المسار المطلوب (<code>{currentPath}</code>) غير متوفر في النظام.</p>
      <Link to="/" className="btn-primary" style={{ maxWidth: '200px' }}>
        العودة للرئيسية
      </Link>
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <RouterProvider>
        <OpeningExperience />
        <AppRoutes />
      </RouterProvider>
    </AuthProvider>
  );
};

export default App;
